// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getBundle } from '@/bundle/api/get-bundle';
import { BundlesFilter, getBundles } from '@/bundle/api/get-bundles';
import { BundleSortProperty } from '@/bundle/api/types';
import { SortDirection } from '@/constants/sort';
import { ONE_MINUTE_MS, TWO_MINUTES_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Query key for the bundles list.
 */
export const bundlesQueryKey = ['bundles'] as const;

/**
 * Query key for a single bundle detail.
 */
export const bundleQueryKey = (key: string) => ['bundle', key] as const;

/**
 * Options for the useBundles hook.
 */
interface UseBundlesOptions {
  query?: string;
  archived?: boolean;
  sort?: BundleSortProperty;
  direction?: SortDirection;
  page?: number;
  limit?: number;
}

/**
 * React hook for fetching and managing the bundles list.
 * Uses React Query with suspense mode for data fetching.
 */
export function useBundles(options: UseBundlesOptions = {}) {
  const filter: BundlesFilter = {
    query: options.query,
    archived: options.archived,
    sort: options.sort,
    direction: options.direction,
    page: options.page,
    limit: options.limit,
  };

  const { data, ...query } = useSuspenseQuery({
    queryKey: [...bundlesQueryKey, filter],
    queryFn: () => getBundles(filter),
    staleTime: ONE_MINUTE_MS,
    refetchInterval: TWO_MINUTES_MS,
  });

  return {
    bundles: data.results,
    total: data.totalCount,
    ...query,
  };
}

/**
 * React hook for fetching a single bundle's details.
 * Uses React Query with suspense mode for data fetching.
 */
export function useBundle(key: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: bundleQueryKey(key),
    queryFn: () => getBundle(key),
    staleTime: ONE_MINUTE_MS,
  });

  return {
    bundle: data.bundle,
    ...query,
  };
}
